<template>
  <div
    ref="viewport"
    class="org-chart-canvas"
    :class="{ 'org-chart-canvas--dragging': dragging }"
    tabindex="0"
    role="region"
    aria-label="Organization chart canvas. Click a person to show their team. Drag to pan, scroll to zoom."
    @wheel.prevent="onWheel"
    @mousedown="onPointerDown"
    @mousemove="onPointerMove"
    @mouseup="onPointerUp"
    @mouseleave="onPointerUp"
    @keydown="onKeyDown"
  >
    <div class="org-chart-canvas__hint d-none d-md-flex">
      <feather-icon icon="MoveIcon" size="12" class="mr-25" />
      Click a person to show their team · Drag to pan · Scroll to zoom
    </div>

    <div
      ref="stage"
      class="org-chart-canvas__stage"
      :style="stageStyle"
    >
      <div class="org-chart-canvas__forest">
        <div
          v-for="(root, index) in roots"
          :key="root.empIdNorm"
          class="org-chart-canvas__tree"
          :class="{ 'org-chart-canvas__tree--alt': index % 2 === 1 }"
        >
          <org-chart-node
            :node="root"
            :collapsed-set="collapsedSet"
            :highlight-set="highlightSet"
            :selected-norm="selectedNorm"
            @toggle="$emit('toggle', $event)"
            @select="$emit('select', $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OrgChartNode from './OrgChartNode.vue'

const MIN_SCALE = 0.4
const MAX_SCALE = 1.8

export default {
  name: 'OrgChartCanvas',
  components: {
    OrgChartNode,
  },
  props: {
    roots: {
      type: Array,
      default: () => [],
    },
    collapsedSet: {
      required: true,
      validator: v => v instanceof Set || (v && typeof v.has === 'function'),
    },
    highlightSet: {
      default: () => new Set(),
      validator: v => v instanceof Set || (v && typeof v.has === 'function'),
    },
    selectedNorm: {
      type: String,
      default: null,
    },
    scale: {
      type: Number,
      default: 1,
    },
    translate: {
      type: Object,
      default: () => ({ x: 0, y: 0 }),
    },
  },
  data() {
    return {
      dragging: false,
      lastX: 0,
      lastY: 0,
      viewAnimating: false,
      viewAnimTimer: null,
    }
  },
  computed: {
    stageStyle() {
      const { x, y } = this.translate
      return {
        transform: `translate(${x}px, ${y}px) scale(${this.scale})`,
        transformOrigin: '0 0',
        transition: this.viewAnimating && !this.dragging
          ? 'transform 0.32s ease'
          : 'none',
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.viewAnimTimer)
  },
  methods: {
    onWheel(e) {
      const delta = e.deltaY > 0 ? -0.08 : 0.08
      this.$emit('zoom-delta', delta)
    },
    onPointerDown(e) {
      if (e.button !== 0) return
      // Pan from empty canvas; leave card/button clicks for select/toggle
      if (e.target && e.target.closest && e.target.closest('button, a, .org-chart-card')) {
        return
      }
      this.viewAnimating = false
      this.dragging = true
      this.lastX = e.clientX
      this.lastY = e.clientY
    },
    onPointerMove(e) {
      if (!this.dragging) return
      const dx = e.clientX - this.lastX
      const dy = e.clientY - this.lastY
      this.lastX = e.clientX
      this.lastY = e.clientY
      this.$emit('pan-delta', { dx, dy })
    },
    onPointerUp() {
      this.dragging = false
    },
    onKeyDown(e) {
      const step = e.shiftKey ? 48 : 24
      if (e.key === '+' || e.key === '=') {
        e.preventDefault()
        this.$emit('zoom-delta', 0.1)
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault()
        this.$emit('zoom-delta', -0.1)
      } else if (e.key === '0') {
        e.preventDefault()
        this.$emit('reset-view')
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        this.$emit('pan-delta', { dx: step, dy: 0 })
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        this.$emit('pan-delta', { dx: -step, dy: 0 })
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        this.$emit('pan-delta', { dx: 0, dy: step })
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        this.$emit('pan-delta', { dx: 0, dy: -step })
      }
    },
    /**
     * Compute scale + translate to fit the stage content in the viewport.
     * @returns {{ scale: number, translate: { x: number, y: number } } | null}
     */
    computeFitTransform() {
      const viewport = this.$refs.viewport
      const stage = this.$refs.stage
      if (!viewport || !stage) return null

      const forest = stage.querySelector('.org-chart-canvas__forest')
      const content = forest || stage
      const contentW = content.scrollWidth || content.offsetWidth
      const contentH = content.scrollHeight || content.offsetHeight
      if (!contentW || !contentH) return null

      const pad = 48
      const vw = Math.max(viewport.clientWidth - pad * 2, 120)
      const vh = Math.max(viewport.clientHeight - pad * 2, 120)
      let nextScale = Math.min(vw / contentW, vh / contentH, 1)
      nextScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale))
      nextScale = Math.round(nextScale * 100) / 100

      const scaledW = contentW * nextScale
      const scaledH = contentH * nextScale
      const x = Math.max(0, (viewport.clientWidth - scaledW) / 2)
      const y = Math.max(24, (viewport.clientHeight - scaledH) / 2)

      return {
        scale: nextScale,
        translate: { x: Math.round(x), y: Math.round(y) },
      }
    },
    /**
     * Pan (and gently scale) so the opened person + their visible team sit in
     * the viewport. Uses current on-screen rects so CSS transform pan stays in sync.
     * @param {string} empIdNorm
     * @param {{ adjustScale?: boolean }} [options]
     * @returns {{ scale: number, translate: { x: number, y: number } } | null}
     */
    computeFocusTransform(empIdNorm, options = {}) {
      const viewport = this.$refs.viewport
      const stage = this.$refs.stage
      if (!viewport || !stage || !empIdNorm) return null

      const safe = (typeof CSS !== 'undefined' && CSS.escape)
        ? CSS.escape(empIdNorm)
        : String(empIdNorm).replace(/"/g, '')
      const nodeEl = stage.querySelector(`[data-emp-norm="${safe}"]`)
      if (!nodeEl) return null

      const cardEl = nodeEl.querySelector('.org-chart-card') || nodeEl
      const groupEl = options.includeChildren === false ? cardEl : nodeEl
      const currentScale = this.scale || 1
      const pad = 36
      const vw = viewport.clientWidth
      const vh = viewport.clientHeight
      if (!vw || !vh) return null

      viewport.scrollLeft = 0
      viewport.scrollTop = 0

      const stageRect = stage.getBoundingClientRect()
      const groupRect = groupEl.getBoundingClientRect()
      const cardRect = cardEl.getBoundingClientRect()
      if (!stageRect.width && !stageRect.height) return null

      const toLocal = rect => ({
        x: (rect.left - stageRect.left) / currentScale,
        y: (rect.top - stageRect.top) / currentScale,
        w: rect.width / currentScale,
        h: rect.height / currentScale,
      })
      const group = toLocal(groupRect)
      const card = toLocal(cardRect)

      let nextScale = currentScale
      if (options.adjustScale !== false) {
        const fitScale = Math.min(
          (vw - pad * 2) / Math.max(group.w, 1),
          (vh - pad * 2) / Math.max(group.h, 1),
          1,
        )
        const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, fitScale))
        // Only ease zoom out so a newly opened team can fit; never zoom in past current.
        nextScale = Math.min(currentScale, clamped)
        nextScale = Math.round(nextScale * 100) / 100
      }

      const scaledGroupW = group.w * nextScale
      const scaledGroupH = group.h * nextScale
      const scaledCardW = card.w * nextScale

      let x
      if (scaledGroupW <= vw - pad * 2) {
        x = (vw - scaledGroupW) / 2 - group.x * nextScale
      } else {
        x = (vw - scaledCardW) / 2 - card.x * nextScale
      }

      let y
      if (scaledGroupH <= vh - pad * 2) {
        y = (vh - scaledGroupH) / 2 - group.y * nextScale
      } else {
        y = pad - group.y * nextScale
      }

      return {
        scale: nextScale,
        translate: { x: Math.round(x), y: Math.round(y) },
      }
    },
    beginViewAnimation() {
      this.viewAnimating = true
      clearTimeout(this.viewAnimTimer)
      this.viewAnimTimer = setTimeout(() => {
        this.viewAnimating = false
      }, 360)
    },
  },
}
</script>

<style lang="scss" scoped>
.org-chart-canvas {
  position: relative;
  width: 100%;
  height: calc(100vh - 240px);
  min-height: 400px;
  overflow: hidden;
  background-color: #d5e3e0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.28), rgba(255, 255, 255, 0.28)),
    radial-gradient(circle at 1px 1px, rgba(0, 87, 79, 0.16) 1.15px, transparent 0);
  background-size: auto, 20px 20px;
  border: 1px solid #b7c7c3;
  border-radius: 0.5rem;
  box-shadow: none;
  cursor: grab;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #00ac99;
  }

  &--dragging {
    cursor: grabbing;
    user-select: none;
  }

  &__hint {
    position: absolute;
    top: 0.65rem;
    right: 0.75rem;
    z-index: 2;
    align-items: center;
    padding: 0.2rem 0.55rem;
    font-size: 0.7rem;
    color: #4b465c;
    background: rgba(255, 255, 255, 0.94);
    border: 1px solid rgba(0, 122, 110, 0.18);
    border-radius: 999px;
    pointer-events: none;
    box-shadow: none;
  }

  &__stage {
    display: block;
    width: max-content;
    min-width: 100%;
    padding: 2rem 2rem 2.75rem;
    will-change: transform;
    background: transparent;
  }

  &__forest {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2.5rem;
    width: max-content;
    min-width: 100%;
  }

  &__tree {
    display: flex;
    justify-content: center;
    width: max-content;
    margin: 0 auto;
    padding: 0.25rem;
    background: transparent;
    border: none;
    box-shadow: none;

    &--alt {
      background: transparent;
      border: none;
    }
  }

  @media (max-width: 991.98px) {
    height: calc(100vh - 280px);
    min-height: 360px;

    &__stage {
      padding: 1.25rem 1rem 2rem;
    }

    &__forest {
      gap: 1.75rem;
    }

    &__tree {
      padding: 0.15rem;
    }
  }
}
</style>
